import type { Journey, PixelConfig } from './types';
import { isInternalDomain, log, safeBase64Decode, safeBase64Encode } from './utils';
import { loadJourney, mergeJourney } from './journey';

const PARAM_NAME = '_tdg';

/**
 * INGESTION: On pixel boot, check if the current URL has a _tdg param.
 * If so, decode, merge into our journey, and strip the param so it doesn't
 * leak into analytics or get bookmarked.
 */
export function ingestIncomingJourney(config: PixelConfig): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const payload = params.get(PARAM_NAME);
  if (!payload) return;

  const decoded = safeBase64Decode(payload);
  if (!decoded) {
    log('failed to decode _tdg payload');
    return;
  }
  try {
    const incoming = JSON.parse(decoded) as Journey;
    mergeJourney(incoming, config);
    log('merged cross-domain journey');
  } catch (e) {
    log('invalid _tdg payload', e);
  } finally {
    // Strip _tdg from the URL bar
    params.delete(PARAM_NAME);
    const remaining = params.toString();
    const newUrl =
      window.location.pathname +
      (remaining ? '?' + remaining : '') +
      window.location.hash;
    try {
      window.history.replaceState(null, '', newUrl);
    } catch {
      /* some browsers/contexts disallow */
    }
  }
}

/**
 * OUTBOUND DECORATION: For each <a> pointing to an internal TDG domain,
 * append _tdg=<journey> at click time. We attach at click time (not preemptively)
 * so the journey is always current, and we don't pollute non-clicked links.
 */
export function installLinkDecorator(config: PixelConfig): void {
  if (typeof document === 'undefined') return;

  const handler = (e: MouseEvent) => {
    const target = (e.target as HTMLElement | null)?.closest('a') as HTMLAnchorElement | null;
    if (!target || !target.href) return;
    if (target.hasAttribute('data-tdg-skip')) return;

    // Only same-org domains we own
    if (!isInternalDomain(target.href, config.internalDomains)) return;

    // Don't decorate same-origin links — same localStorage already
    try {
      const targetOrigin = new URL(target.href).origin;
      if (targetOrigin === window.location.origin) return;
    } catch {
      return;
    }

    const journey = loadJourney();
    if (!journey.touchpoints.length) return;

    const payload = safeBase64Encode(JSON.stringify(journey));
    try {
      const url = new URL(target.href);
      url.searchParams.set(PARAM_NAME, payload);
      target.href = url.toString();
      log('decorated link →', target.href);
    } catch {
      /* skip */
    }
  };

  // Capture phase so we mutate before navigation
  document.addEventListener('click', handler, { capture: true });
  // Also handle middle-click / cmd-click
  document.addEventListener('auxclick', handler, { capture: true });
}
